import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { heroImageStyle } from "./FestivalShowcase";

// Simple, sober cinematic entry: the Festival hero visual (chair / laurels) darkened,
// the "KARAMS FESTIVAL" title, and an elegant blue halo. Duration is admin-configurable.
// It reuses the SAME background adjustments as the Hero (heroSettings) for consistency.
export const FestivalEntryTransition = ({ active, onDone, image, heroSettings, duration = 1.1, accent = "#2e6bff" }) => {
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => onDone?.(), Math.max(0.3, duration) * 1000);
    return () => clearTimeout(t);
  }, [active, onDone, duration]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="festival-entry-transition"
          className="fixed inset-0 z-[10000] bg-black flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}>
          {image && (
            <motion.img src={image} alt="" className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: heroImageStyle(heroSettings || {}).filter, objectPosition: heroImageStyle(heroSettings || {}).objectPosition }}
              initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 0.6 }}
              transition={{ duration: Math.max(0.3, duration) * 0.9, ease: "easeOut" }} />
          )}
          <div className="absolute inset-0 bg-black/55" />
          {/* elegant edition-colour halo behind the title */}
          <motion.div className="absolute w-[62vw] h-[62vw] max-w-[620px] max-h-[620px] rounded-full"
            style={{ background: `radial-gradient(circle, color-mix(in srgb, ${accent} 30%, transparent), transparent 62%)` }}
            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1.1 }}
            transition={{ duration: 0.7, delay: 0.08, ease: "easeOut" }} />
          <motion.div className="relative text-center px-6"
            initial={{ opacity: 0, scale: 1.06, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.55, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}>
            <div className="font-sans font-bold uppercase tracking-[0.22em] text-white text-3xl sm:text-5xl md:text-6xl leading-none"
              style={{ textShadow: `0 0 26px color-mix(in srgb, ${accent} 75%, transparent), 0 0 60px color-mix(in srgb, ${accent} 35%, transparent)` }}>
              Karams <span style={{ color: accent }}>Festival</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Whether the user asked the OS to reduce motion.
export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
